import React, { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { api } from '../../lib/api';
import type { Feature } from '../../types';
import {
  Spinner, EmptyState, Modal, Alert, ConfirmDialog,
  inputClass, labelClass, primaryBtn, secondaryBtn,
} from '../../components/ui';

export default function Features() {
  const [features, setFeatures] = useState<Feature[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ name: '', category: '', display_order: 0 });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get<Feature[]>('/features'),
      api.get<string[]>('/features/categories'),
    ])
      .then(([f, c]) => { setFeatures(f); setCategories(c); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await api.post('/features', form);
      setModalOpen(false);
      setForm({ name: '', category: '', display_order: 0 });
      load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await api.delete(`/features/${id}`);
      setDeleteId(null);
      load();
    } catch {
      setDeleteId(null);
    }
  };

  const grouped = React.useMemo(() => {
    const map: Record<string, Feature[]> = {};
    features.forEach((f) => {
      const key = f.category || 'Other';
      if (!map[key]) map[key] = [];
      map[key].push(f);
    });
    return map;
  }, [features]);

  if (loading) return <Spinner label="Loading features..." />;

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
        <h1 className="text-2xl font-bold">Property Features</h1>
        <button onClick={() => { setError(''); setModalOpen(true); }} className={primaryBtn}>
          <Plus className="h-4 w-4" /> Add Feature
        </button>
      </div>

      {Object.keys(grouped).length === 0 ? (
        <EmptyState message="No features yet." />
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Object.entries(grouped).map(([category, items]) => (
            <div key={category} className="bg-white p-5 rounded-lg shadow-lg">
              <h2 className="font-semibold mb-3">{category}</h2>
              <div className="space-y-2">
                {items.map((f) => (
                  <div key={f.id} className="flex items-center justify-between gap-2 flex-wrap text-sm">
                    <span>{f.name}</span>
                    <button onClick={() => setDeleteId(f.id)} className="p-1.5 rounded hover:bg-red-50 text-red-400 hover:text-red-600">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="Add Feature">
        {error && <Alert>{error}</Alert>}
        <form onSubmit={handleCreate} className="space-y-4">
          <div>
            <label className={labelClass}>Name *</label>
            <input className={inputClass} required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <label className={labelClass}>Category</label>
            <select className={inputClass} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              <option value="">Other</option>
              {categories.filter((c) => c !== 'Other').map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Display Order</label>
            <input type="number" min={0} className={inputClass} value={form.display_order} onChange={(e) => setForm({ ...form, display_order: Number(e.target.value) })} />
          </div>
          <div className="flex justify-end space-x-3">
            <button type="button" onClick={() => setModalOpen(false)} className={secondaryBtn}>Cancel</button>
            <button type="submit" disabled={saving} className={primaryBtn}>{saving ? 'Adding...' : 'Add Feature'}</button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={() => deleteId && handleDelete(deleteId)}
        title="Delete Feature"
        message="Are you sure you want to delete this feature?"
      />
    </div>
  );
}
